// frontend/src/api/onboarding-measurement-api.ts
//
// API helpers for the async Stage 4 measurement run.
// REPLACE your existing version of this file — the WebSocket URL is
// simplified from /onboarding/ws/onboarding/measurement to /onboarding/ws/measurement.

import { apiFetch } from "@/api/api-fetch";
import { buildWsUrl } from "@/api/url-route";

// ── Types ────────────────────────────────────────────────────────────────

export type MeasurementRunStatus =
  | "pending"
  | "running"
  | "completed"
  | "cancelled"
  | "failed";

export interface MeasurementBatchConfig {
  queries: number;
  reps: number;
  models: string[];
  total_calls?: number;
  // "baseline" (versions the imported baseline ran with) or "default" (current
  // live versions). Omitted → backend defaults to "baseline".
  version_mode?: "baseline" | "default";
  // Optional per-lane model overrides: { lane: version }. Backend re-validates
  // each against its allowlist; a lane absent here runs its version_mode default.
  model_overrides?: Record<string, string>;
  // Optional per-lane endpoint overrides (#90): { lane: "sonar" | "agent" }.
  // Today only meaningful for perplexity (Sonar chat completions, retiring
  // 2026-09-27, vs. the Agent API). A lane absent here runs its catalog
  // default_endpoint.
  endpoint_overrides?: Record<string, string>;
  // The Stage-5 judge for this run's automatic scoring pass, pinned at Stage 4
  // before collection starts. Omitted -> the registry default judge.
  scorer_model_id?: string;
  // How this run's Stage-5 scoring pass is sent: "batch" (Anthropic Message
  // Batches, half price, results when the batch ends) or "direct" (one
  // synchronous request per response, full price, minutes). Omitted -> the
  // backend decides, which is batch for ordinary scoring.
  scoring_mode?: "batch" | "direct";
  // Optional per-lane web-search selection: { lane: boolean }. Whether each
  // provider is probed web-grounded (true) or static (false). Sparse — a lane
  // omitted falls back to the catalog default. The backend resolves the full
  // map and stores it on the run as batch_config.web_search.
  web_search_overrides?: Record<string, boolean>;
  // Backend-resolved, read-only: the FULL per-lane grounding map this run
  // actually executed with. Never sent by the client.
  web_search?: Record<string, boolean>;
  // Backend-resolved, read-only: the customer's standing per-lane version pins
  // as of this run. Never sent by the client — the server resolves them from
  // the customer's earlier runs so a client's reads keep reproducing its first
  // read as the catalog moves on.
  version_pins?: Record<string, string>;
  // Backend-set, read-only, Smoke calibration runs only (#138): which round of
  // which calibration this run is. A run without it is round 1.
  calibration_round?: { number: number; previous_run_id: number | null; root_run_id?: number | null };
}

/** Which model versions a customer's NEXT run defaults to.
 *
 * `pinned` clients (no baseline of their own, or already carrying pins) start
 * on "default" (Current) and reuse `pins` for every later read. Existing
 * clients, anchored to the imported baseline, come back default_mode
 * "baseline" with no pins — unchanged from before this existed. */
export interface MeasurementVersionPolicy {
  default_mode: "baseline" | "default";
  pinned: boolean;
  pins: Record<string, string>;
  /** Pinned clients only: what a lane with no pin yet will be pinned to on
   * its first read — the Global Settings "Measurement defaults for new
   * clients" (e.g. perplexity → "low"). Empty for unpinned existing clients,
   * which never read that setting. */
  first_read_defaults?: Record<string, string>;
  /** Present once the series has been rebaselined: the lanes it is read on
   *  since its latest rebaseline, and the battery version. */
  series_lanes?: string[];
  battery_version?: number;
  /** An applied rebaseline no read has taken yet: the NEXT client read is
   *  forced onto these lanes and pins and becomes the new baseline. */
  pending_rebaseline?: {
    id: number;
    battery_version: number;
    lanes: string[];
    pins: Record<string, string>;
  } | null;
}

export interface MeasurementRun {
  id: number;
  customer_id: number;
  task_id: string | null;
  status: MeasurementRunStatus;
  /**
   * Per-customer run number (1-based). This is what the end user should see —
   * NOT `id`, which is the global DB primary key and leaks the total run count
   * across every customer. Falls back to `id` only if unexpectedly null.
   */
  run_sequence_number: number | null;
  /** Audited run_label enum (e.g. baseline / re-measure). */
  run_label: string | null;
  /** Optional free-text display name; overrides "Run #N" when set. */
  run_name: string | null;
  batch_config: MeasurementBatchConfig;
  progress_completed: number;
  progress_total: number;
  cost_so_far_usd: number;
  started_at: string | null;
  finished_at: string | null;
  cancellation_requested: boolean;
  error_message: string | null;
  summary: {
    total_calls: number;
    successful: number;
    failed: number;
    cost_usd: number;
    /** Google AI Mode empty-answer rollup (#108). Google sometimes returns a
     *  billed AI Mode container with nothing captured in it; those rows used
     *  to be scored as a real "not mentioned" and pulled the lane's rates
     *  down. They are now retried, and a row still empty at the cap is a
     *  FAILED measurement excluded from scoring and from the rates.
     *  All-zero on a run with no AI Mode lane, and on runs collected before
     *  #108. */
    aimode_retries?: AiModeRetrySummary;
  } | null;
  /** Smoke-stage rubric calibration summary (present on smoke_calibration runs). */
  calibration?: CalibrationSummary | null;
  calibration_acknowledged?: boolean;
}

export interface AiModeRetrySummary {
  /** AI Mode rows in the run. */
  aimode_rows: number;
  /** Rows whose FINAL answer was empty. A row filled by a retry is not one. */
  no_answer_rows: number;
  /** Empty rows recorded as failed measurements — excluded from the rates. */
  failed_rows: number;
  /** Rows that took more than one attempt. */
  retried_rows: number;
  /** Total retry calls made across the run. */
  retry_attempts: number;
  /** What those retry calls cost, provider-reported. */
  retry_cost_usd: number;
  /** Why each retry loop stopped, counted:
   *  filled | cap | budget | deadline | error. A non-zero `budget` means the
   *  run stopped retrying because its retry spend cap was reached. */
  stopped: Record<string, number>;
}

// ── Smoke-stage rubric calibration ─────────────────────────────────────────

export type CalibrationFlagKind = "saturation" | "stuck" | "coverage" | "thin";

export interface CalibrationVariableRow {
  variable_id: string;
  claim: string;
  applicable_cells: number;
  /** Distinct sampled questions with a scored cell for this belief; PASS
   *  needs at least two (#138). Absent on summaries from before. */
  applicable_questions?: number;
  score_0: number;
  score_1: number;
  score_2: number;
  pct_2: number;
  pct_0: number;
  flags: CalibrationFlagKind[];
}

export interface CalibrationFlag {
  variable_id: string;
  claim: string;
  flag: CalibrationFlagKind;
  reason: string;
}

export interface CalibrationEmptyWarning {
  response_index: number;
  query_id: string | null;
  model: string | null;
  reason: string;
}

export interface CalibrationSummary {
  state: "PASS" | "REVIEW";
  sample_size: number;
  sample_query_ids?: string[];
  mapping_mode: "mapped" | "holistic";
  rubric_version: string;
  /** The question battery this calibration certifies, with rubric_version. */
  battery_hash?: string;
  per_variable: CalibrationVariableRow[];
  flags: CalibrationFlag[];
  empty_response_warnings: CalibrationEmptyWarning[];
  generated_at: string;
  diagnosis?: CalibrationDiagnosis;
  applied?: CalibrationApplied;
  acknowledgement?: CalibrationAcknowledgement;
}

// ── Diagnosis, proposals and rounds (#138) ───────────────────────────────

export type CalibrationCause = "anchor_too_easy" | "anchor_wording" | "no_room" | "category_default";

export interface CalibrationAnchors {
  score_0: string;
  score_1: string;
  score_2: string;
  examples?: Record<"0" | "1" | "2", string>;
}

export interface CalibrationQuote {
  ref: string;
  quote: string;
}

export interface CalibrationAnswer {
  ref: string;
  response_index: number;
  query_id: string;
  model: string | null;
  applicable: boolean;
  score: number | null;
  judge_evidence: string;
  text: string;
  truncated: boolean;
}

export interface CalibrationSampledQuestion {
  id: string;
  text: string;
  form: string;
  persona: string;
  variable_ids: string[];
}

export interface CalibrationQuestionChange {
  id: string;
  before: { text: string; variable_ids: string[] };
  after: { text: string; variable_ids: string[] };
}

export type CalibrationProposalKind = "anchors" | "questions" | "replace_belief";

export interface CalibrationProposal {
  id: string;
  variable_id: string;
  kind: CalibrationProposalKind;
  status: "pending" | "accepted" | "rejected";
  rationale: string;
  grounding: CalibrationQuote[];
  before?: CalibrationAnchors;
  anchors?: CalibrationAnchors;
  questions?: CalibrationQuestionChange[];
  note?: string;
  decision?: {
    action: "accept" | "reject";
    by: string | null;
    at: string;
    note: string;
    edited: boolean;
    content?: { anchors?: CalibrationAnchors; questions?: CalibrationQuestionChange[] };
  };
}

export interface CalibrationBeliefDiagnosis {
  variable_id: string;
  label: string;
  claim: string;
  flags: CalibrationFlagKind[];
  flag: CalibrationFlagKind;
  status: "ready" | "failed" | "skipped";
  error?: string;
  /** Why a skipped belief was not diagnosed. */
  reason?: string;
  cause?: CalibrationCause;
  cause_label?: string;
  diagnosis?: string;
  quotes?: CalibrationQuote[];
  anchors: CalibrationAnchors;
  evidence: { ref: string; quote: string; source?: string | null }[];
  answers: CalibrationAnswer[];
  questions: CalibrationSampledQuestion[];
  proposal?: CalibrationProposal;
}

export interface CalibrationDiagnosis {
  /** "off": the automatic diagnosis was off when the calibration finished, so
   *  nothing was diagnosed and nothing is on its way. */
  status: "ready" | "partial" | "failed" | "none" | "off";
  model?: string;
  cost_usd: number;
  budget_usd?: number;
  generated_at: string;
  error?: string;
  beliefs: CalibrationBeliefDiagnosis[];
  /** Set when the framework fails its approval checks before any proposal
   *  touches it: a rewrite cannot be applied until Stage 3 fixes it. */
  apply_blocked?: { reason: string; message: string; detail: string };
}

export interface CalibrationApplied {
  by: string | null;
  at: string;
  round: number;
  mode: "rerun" | "handed_to_stage3";
  next_run_id: number | null;
  rerun_error?: string;
  /** The automatic re-run was off when this was applied: the admin starts it. */
  rerun_off?: boolean;
  proposals: { id: string; variable_id: string; kind: CalibrationProposalKind; edited: boolean }[];
}

export interface CalibrationAcknowledgement {
  reason: string;
  by: string | null;
  at: string;
  flags: { variable_id: string; flag: CalibrationFlagKind }[];
  empty_responses: number;
}

export interface CalibrationRoundReport {
  run_id: number;
  round: number;
  status: string;
  state: "PASS" | "REVIEW" | null;
  flags: { variable_id: string; flag: CalibrationFlagKind }[];
  empty_responses: number;
  rubric_version: string | null;
  battery_hash: string | null;
  diagnosis_status: string | null;
  proposals: {
    id: string; variable_id: string; kind: CalibrationProposalKind;
    status: string; decided_by: string | null; edited: boolean; note: string;
  }[];
  applied: CalibrationApplied | null;
  acknowledged: boolean;
  acknowledgement: CalibrationAcknowledgement | null;
  cost_usd: number;
  triggered_by: string | null;
  created_at: string | null;
  finished_at: string | null;
}

export interface CalibrationHistory {
  calibrations: { root_run_id: number; rounds: CalibrationRoundReport[]; spent_usd: number }[];
  max_rounds: number;
  spend_cap_usd: number;
}

export interface CalibrationGate {
  open: boolean;
  reason: string;
  /** Set when the gate is open on a calibration that predates the battery
   *  hash: its rubric was matched, its battery could not be. */
  grandfathered_run_id?: number | null;
  calibration_run: MeasurementRun | null;
  /** True while the calibration run has finished probing but its scoring pass
   *  is still running, which is exactly when calibration_json does not exist
   *  yet. A summary-less completed run is only a failure when this is false. */
  scoring_in_flight?: boolean;
  /** The (rubric_version, battery_hash) the next read would carry. */
  pair?: { rubric_version: string; battery_hash: string };
  history?: CalibrationHistory | null;
  /** Whether a diagnosis and a re-run start on their own: Global Settings. */
  automation?: { diagnosis: boolean; rerun: boolean };
}

export interface SmokeCalibrationValues {
  diagnosis_enabled: boolean;
  auto_rerun_enabled: boolean;
  spend_cap_usd: number;
}

export interface SmokeCalibrationSettings extends SmokeCalibrationValues {
  source: "admin" | "built_in";
  defaults: SmokeCalibrationValues;
}

export function fetchSmokeCalibrationSettings(): Promise<SmokeCalibrationSettings> {
  return apiFetch("/admin/smoke-calibration-settings");
}

/** A setting left out stays as it is; null goes back to the built-in default. */
export function saveSmokeCalibrationSettings(
  values: { [K in keyof SmokeCalibrationValues]?: SmokeCalibrationValues[K] | null },
): Promise<SmokeCalibrationSettings> {
  return apiFetch("/admin/smoke-calibration-settings", { method: "PUT", body: JSON.stringify(values) });
}

// ── REST endpoints ───────────────────────────────────────────────────────

export async function fetchCalibrationGate(
  customerId: number,
): Promise<CalibrationGate> {
  return apiFetch(`/onboarding/customers/${customerId}/calibration/gate`);
}

export async function startCalibration(
  customerId: number,
  opts: { sample_size?: number; batch_config?: Partial<MeasurementBatchConfig> } = {},
): Promise<{ run: MeasurementRun }> {
  return apiFetch(`/onboarding/customers/${customerId}/calibration/run`, {
    method: "POST",
    body: JSON.stringify({
      sample_size: opts.sample_size,
      batch_config: opts.batch_config ?? null,
    }),
  });
}

export async function acknowledgeCalibration(
  customerId: number,
  runId: number,
  reason: string,
): Promise<{ run: MeasurementRun }> {
  return apiFetch(
    `/onboarding/customers/${customerId}/runs/${runId}/calibration/acknowledge`,
    { method: "POST", body: JSON.stringify({ reason }) },
  );
}

export interface CalibrationDecisionResult {
  run: MeasurementRun;
  /** Set when this decision completed the round and it was applied. */
  applied: CalibrationApplied | null;
  next_run: MeasurementRun | null;
  /** Set when the round was ready but applying it was refused. */
  apply_error: string | null;
}

export async function decideCalibrationProposal(
  customerId: number,
  runId: number,
  proposalId: string,
  body: {
    action: "accept" | "reject";
    edited?: {
      anchors?: CalibrationAnchors;
      questions?: { id: string; text: string; variable_ids: string[] }[];
    };
    note?: string;
  },
): Promise<CalibrationDecisionResult> {
  return apiFetch(
    `/onboarding/customers/${customerId}/runs/${runId}/calibration/proposals/${encodeURIComponent(proposalId)}/decision`,
    { method: "POST", body: JSON.stringify(body) },
  );
}

export async function applyCalibrationRound(
  customerId: number,
  runId: number,
): Promise<{ applied: CalibrationApplied; next_run: MeasurementRun | null }> {
  return apiFetch(
    `/onboarding/customers/${customerId}/runs/${runId}/calibration/apply`,
    { method: "POST" },
  );
}

export async function rediagnoseCalibration(
  customerId: number,
  runId: number,
): Promise<{ run: MeasurementRun }> {
  return apiFetch(
    `/onboarding/customers/${customerId}/runs/${runId}/calibration/diagnose`,
    { method: "POST" },
  );
}

export async function fetchMeasurementState(
  customerId: number,
): Promise<{ run: MeasurementRun | null }> {
  return apiFetch(`/onboarding/customers/${customerId}/measurement`);
}

export async function fetchMeasurementVersionPolicy(
  customerId: number,
): Promise<MeasurementVersionPolicy> {
  return apiFetch(
    `/onboarding/customers/${customerId}/measurement/version-policy`,
  );
}

export async function startMeasurement(
  customerId: number,
  batchConfig: MeasurementBatchConfig,
  options: { restart?: boolean } = {},
): Promise<{ run: MeasurementRun }> {
  return apiFetch(`/onboarding/customers/${customerId}/measurement`, {
    method: "POST",
    body: JSON.stringify({
      batch_config: {
        queries: batchConfig.queries,
        reps: batchConfig.reps,
        models: batchConfig.models,
        version_mode: batchConfig.version_mode,
        model_overrides: batchConfig.model_overrides,
        endpoint_overrides: batchConfig.endpoint_overrides,
        scorer_model_id: batchConfig.scorer_model_id,
        // Must be forwarded for the same reason web_search_overrides must: this
        // body is an explicit field whitelist, so a field added to
        // MeasurementBatchConfig but not listed here is silently dropped and
        // the run falls back to the default transport while the Stage-4 control
        // still reads as chosen.
        scoring_mode: batchConfig.scoring_mode,
        // Must be forwarded: this body is an explicit field whitelist, so a
        // field added to MeasurementBatchConfig but not listed here is silently
        // dropped and the run falls back to the catalog grounding defaults
        // while the Stage-4 toggles still read as overridden.
        web_search_overrides: batchConfig.web_search_overrides,
      },
      restart: !!options.restart,
    }),
  });
}

export async function cancelMeasurement(
  runId: number,
): Promise<{ run: MeasurementRun }> {
  return apiFetch(`/onboarding/measurement/${runId}/cancel`, {
    method: "POST",
  });
}

export async function resumeMeasurement(
  customerId: number,
  runId: number,
): Promise<{ run: MeasurementRun }> {
  return apiFetch(
    `/onboarding/customers/${customerId}/measurement/${runId}/resume`,
    { method: "POST" },
  );
}

// ── WebSocket — resumable live stream ────────────────────────────────────

export type MeasurementWsMessage =
  | { type: "snapshot"; run: MeasurementRun }
  | { type: "no_run" }
  | { type: "measurement_started"; total: number; config: MeasurementBatchConfig; resumed?: boolean; completed?: number; cost_so_far?: number; timestamp: string }
  | { type: "measurement_progress"; completed: number; total: number; percent: number; cost_so_far: number; timestamp: string }
  | { type: "measurement_complete"; completed: number; total: number; cost_usd: number; summary: any; customer: any; timestamp: string }
  | { type: "measurement_cancelled"; completed: number; total: number; cost_usd: number; timestamp: string }
  | { type: "heartbeat"; timestamp: string }
  | { type: "error"; message: string; trace?: string; timestamp: string };

export function openMeasurementWs(args: {
  customerId: number;
  onEvent: (msg: MeasurementWsMessage) => void;
  onClose?: () => void;
  onError?: (e: Event) => void;
}): WebSocket {
  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token") ||
    "";
  const base = buildWsUrl();

  const params = new URLSearchParams({
    token,
    customer_id: String(args.customerId),
  });

  // Simplified path: /onboarding/ws/measurement
  // Full URL on server side: /api/onboarding/ws/measurement
  const url = `${base}/onboarding/ws/measurement?${params.toString()}`;
  const ws = new WebSocket(url);

  ws.onmessage = (e) => {
    try {
      args.onEvent(JSON.parse(e.data));
    } catch {
      // ignore malformed
    }
  };
  if (args.onClose) ws.onclose = args.onClose;
  if (args.onError) ws.onerror = args.onError;
  return ws;
}